import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { Flag, CheckCircle2, AlertCircle } from 'lucide-react';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';
import {
  useReportListingMutation,
  useReportUserMutation,
} from '../../ducks/reportApi';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

type ReportTarget = 'listing' | 'user';

const REASON_KEYS: Record<ReportTarget, string[]> = {
  listing: ['misleading', 'spam', 'prohibited', 'pricing', 'unsafe', 'other'],
  user: ['suspicious', 'harassment', 'off_platform', 'fake_account', 'bad_faith', 'other'],
};

type ReportDialogProps = {
  targetType: ReportTarget;
  targetId: string;
  targetName?: string;
  /** Trigger element. Required in uncontrolled mode; ignored if `open`
   * + `onOpenChange` are provided. */
  children?: React.ReactElement<{ onClick?: (e: React.MouseEvent) => void }>;
  /** Controlled-mode open state. Pass with `onOpenChange` when the dialog
   * is opened by something the dialog itself can't wrap (e.g. a dropdown
   * menu item, a context menu, or a parent-level handler). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const ReportDialog = ({
  targetType,
  targetId,
  targetName,
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: ReportDialogProps) => {
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const routes = useRouteConfiguration();
  const isAuthenticated = useSelector((state: any) => state.auth?.isAuthenticated);
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined && controlledOnOpenChange !== undefined;
  const open = isControlled ? !!controlledOpen : internalOpen;
  const setOpen = isControlled ? controlledOnOpenChange! : setInternalOpen;
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [reportListing, listingState] = useReportListingMutation();
  const [reportUser, userState] = useReportUserMutation();
  const mutationState = targetType === 'listing' ? listingState : userState;
  const inProgress = mutationState.isLoading;
  const errorMaybe = mutationState.error as { data?: { message?: string } } | undefined;

  const reasons = REASON_KEYS[targetType];

  useEffect(() => {
    if (!open) {
      setReason('');
      setDetails('');
      setSubmitted(false);
      mutationState.reset?.();
    }
    // mutationState identity changes every render; we only care about `open`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const trigger = children
    ? React.cloneElement(children, {
        onClick: (e: React.MouseEvent) => {
          children.props.onClick?.(e);
          if (!isAuthenticated) {
            const from = `${location.pathname}${location.search}${location.hash}`;
            history.push(createResourceLocatorString('SignupPage', routes, {}, {}), { from });
            return;
          }
          setOpen(true);
        },
      })
    : null;

  const handleSubmit = async () => {
    if (!reason || inProgress) return;
    try {
      if (targetType === 'listing') {
        await reportListing({ listingId: targetId, reason, details: details.trim() || undefined }).unwrap();
      } else {
        await reportUser({ userId: targetId, reason, details: details.trim() || undefined }).unwrap();
      }
      setSubmitted(true);
    } catch {
      // surfaces via mutationState.error
    }
  };

  const targetLabelText = intl.formatMessage({
    id: targetType === 'listing' ? 'ReportDialog.targetLabel.listing' : 'ReportDialog.targetLabel.user',
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {trigger}
      <DialogContent
        className="max-w-[520px] border-[#ddd6cd] bg-[#fcfaf7] p-0 shadow-[0_24px_70px_-42px_rgba(23,21,19,0.35)]"
        data-testid={`dialog-report-${targetType}`}
      >
        <DialogHeader className="border-b border-[#ece6de] px-5 py-4 sm:px-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-destructive/15 bg-destructive/10 text-destructive">
              <Flag className="h-4 w-4" />
            </div>
            <div className="min-w-0 text-left">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#7e7468]">
                <FormattedMessage id="ReportDialog.eyebrow" />
              </p>
              <DialogTitle
                className="mt-1 text-xl font-heading font-bold uppercase text-[#171513]"
                data-testid={`text-report-${targetType}-title`}
              >
                <FormattedMessage
                  id={
                    targetType === 'listing'
                      ? 'ReportDialog.titleListing'
                      : 'ReportDialog.titleUser'
                  }
                />
              </DialogTitle>
              <DialogDescription
                className="mt-2 text-sm leading-6 text-[#5f584e]"
                data-testid={`text-report-${targetType}-description`}
              >
                <FormattedMessage
                  id={
                    targetType === 'listing'
                      ? 'ReportDialog.descriptionListing'
                      : 'ReportDialog.descriptionUser'
                  }
                  values={{ name: targetName || '' }}
                />
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {submitted ? (
          <div className="px-5 py-8 text-center sm:px-6" data-testid="panel-report-success">
            <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
            <p className="mt-3 text-base font-semibold text-[#171513]">
              <FormattedMessage id="ReportDialog.successTitle" />
            </p>
            <p className="mt-1 text-sm text-[#5f584e]">
              <FormattedMessage id="ReportDialog.successDescription" />
            </p>
          </div>
        ) : (
          <div className="space-y-4 px-5 py-4 sm:px-6">
            <div className="space-y-2">
              <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7e7468]">
                <FormattedMessage id="ReportDialog.reasonLegend" />
              </label>
              <Select value={reason} onValueChange={setReason} disabled={inProgress}>
                <SelectTrigger
                  className="h-11 border-[#ddd6cd] bg-white text-sm"
                  data-testid={`select-report-reason-${targetType}`}
                >
                  <SelectValue
                    placeholder={intl.formatMessage(
                      { id: 'ReportDialog.reasonPlaceholder' },
                      { target: targetLabelText }
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {reasons.map(key => (
                    <SelectItem
                      key={key}
                      value={key}
                      data-testid={`option-report-reason-${targetType}-${key}`}
                    >
                      {intl.formatMessage({ id: `ReportDialog.reason.${targetType}.${key}` })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7e7468]">
                <FormattedMessage id="ReportDialog.detailsLabel" />
              </label>
              <Textarea
                value={details}
                onChange={e => setDetails(e.target.value)}
                placeholder={intl.formatMessage({ id: 'ReportDialog.detailsPlaceholder' })}
                maxLength={1000}
                disabled={inProgress}
                className="min-h-[132px] resize-none border-[#ddd6cd] bg-white"
                data-testid={`textarea-report-details-${targetType}`}
              />
            </div>

            {errorMaybe && (
              <div
                className="flex items-start gap-2 rounded-xl bg-destructive/10 px-3 py-2 text-sm text-destructive"
                data-testid="text-report-error"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>
                  {errorMaybe.data?.message || (
                    <FormattedMessage id="ReportDialog.errorGeneric" />
                  )}
                </span>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="border-t border-[#ece6de] bg-white px-5 py-4 sm:px-6">
          {submitted ? (
            <Button
              type="button"
              onClick={() => setOpen(false)}
              className="font-heading uppercase"
              data-testid="button-close-report"
            >
              <FormattedMessage id="ReportDialog.close" />
            </Button>
          ) : (
            <>
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={inProgress}
                data-testid={`button-cancel-report-${targetType}`}
              >
                <FormattedMessage id="ReportDialog.cancel" />
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={!reason || inProgress}
                className="font-heading uppercase"
                data-testid={`button-submit-report-${targetType}`}
              >
                {inProgress ? (
                  <FormattedMessage id="ReportDialog.submitting" />
                ) : (
                  <FormattedMessage id="ReportDialog.submit" />
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReportDialog;
